'use client'

import { memo, useEffect, useMemo, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

type ScenePalette = {
  skyTop: string
  skyMid: string
  skyLow: string
  star: string
  moon: string
  cloud: string
  hillA: string
  hillB: string
  ground: string
  path: string
  pathEdge: string
  hut: string
  hutRoof: string
  field: string
  foliage: string
  foliageDark: string
  stoneMid: string
  stoneLight: string
  neon: string
  neonSoft: string
  tower: string
}

/** baby: one logical pixel = 4 SVG units */
const px = (n: number) => n * 4

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

const smooth = (t: number) => t * t * (3 - 2 * t)

const eraFromTime = (t: number): number => {
  if (t < 0.25) return 0
  if (t < 0.45) return smooth((t - 0.25) / 0.2)
  if (t < 0.7) return 1
  if (t < 0.9) return smooth(1 - (t - 0.7) / 0.2)
  return 0
}

type BuildingShape = 'block' | 'step' | 'spire' | 'antenna' | 'wide'
type DepthLayer = 'far' | 'mid' | 'near'

type Building = {
  id: string
  layer: DepthLayer
  x: number
  w: number
  vY: number
  vH: number
  cY: number
  cH: number
  shape: BuildingShape
}

/**
 * Depth layers (drawn back → front):
 * far = distant night skyline, mid = arch city, near = closer towers at sides
 */
const BUILDINGS: Building[] = [
  // Far: small, high on horizon (through arch, behind)
  { id: 'f1', layer: 'far', x: 50, w: 4, vY: 52, vH: 4, cY: 30, cH: 26, shape: 'block' },
  { id: 'f2', layer: 'far', x: 56, w: 5, vY: 51, vH: 5, cY: 26, cH: 30, shape: 'spire' },
  { id: 'f3', layer: 'far', x: 63, w: 6, vY: 50, vH: 6, cY: 22, cH: 34, shape: 'step' },
  { id: 'f4', layer: 'far', x: 71, w: 5, vY: 51, vH: 5, cY: 28, cH: 28, shape: 'block' },
  { id: 'f5', layer: 'far', x: 78, w: 7, vY: 50, vH: 6, cY: 20, cH: 36, shape: 'antenna' },
  { id: 'f6', layer: 'far', x: 87, w: 5, vY: 51, vH: 5, cY: 24, cH: 32, shape: 'spire' },
  { id: 'f7', layer: 'far', x: 94, w: 6, vY: 50, vH: 6, cY: 27, cH: 29, shape: 'block' },
  { id: 'f8', layer: 'far', x: 102, w: 4, vY: 52, vH: 4, cY: 32, cH: 24, shape: 'step' },

  // Mid: main arch skyline, varied silhouettes
  { id: 'm1', layer: 'mid', x: 48, w: 7, vY: 50, vH: 8, cY: 22, cH: 36, shape: 'step' },
  { id: 'm2', layer: 'mid', x: 56, w: 9, vY: 48, vH: 10, cY: 14, cH: 44, shape: 'spire' },
  { id: 'm3', layer: 'mid', x: 66, w: 12, vY: 46, vH: 12, cY: 8, cH: 50, shape: 'wide' },
  { id: 'm4', layer: 'mid', x: 79, w: 8, vY: 48, vH: 10, cY: 16, cH: 42, shape: 'antenna' },
  { id: 'm5', layer: 'mid', x: 88, w: 10, vY: 47, vH: 11, cY: 12, cH: 46, shape: 'step' },
  { id: 'm6', layer: 'mid', x: 99, w: 7, vY: 50, vH: 8, cY: 18, cH: 40, shape: 'spire' },

  // Near: sides, larger / more present
  { id: 'n1', layer: 'near', x: 1, w: 9, vY: 50, vH: 8, cY: 16, cH: 42, shape: 'wide' },
  { id: 'n2', layer: 'near', x: 11, w: 11, vY: 48, vH: 10, cY: 10, cH: 48, shape: 'antenna' },
  { id: 'n3', layer: 'near', x: 136, w: 11, vY: 48, vH: 10, cY: 12, cH: 46, shape: 'step' },
  { id: 'n4', layer: 'near', x: 148, w: 10, vY: 50, vH: 8, cY: 18, cH: 40, shape: 'spire' },
]

const LAYER_OPACITY: Record<DepthLayer, { village: number; cyber: number }> = {
  far: { village: 0.55, cyber: 0.5 },
  mid: { village: 0.9, cyber: 0.92 },
  near: { village: 1, cyber: 1 },
}

const LAYER_ORDER: DepthLayer[] = ['far', 'mid', 'near']

const FLYING_CARS = [
  { id: 'c1', y: 11, speed: 22, w: 5, trail: 3, layer: 'far' as const },
  { id: 'c2', y: 15, speed: -18, w: 6, trail: 4, layer: 'far' as const },
  { id: 'c3', y: 19, speed: 28, w: 7, trail: 4, layer: 'mid' as const },
  { id: 'c4', y: 13, speed: -24, w: 5, trail: 3, layer: 'mid' as const },
  { id: 'c5', y: 22, speed: 15, w: 8, trail: 5, layer: 'near' as const },
]

const STARS = [
  { x: 6, y: 3 },
  { x: 18, y: 7 },
  { x: 34, y: 4 },
  { x: 52, y: 9 },
  { x: 98, y: 5 },
  { x: 118, y: 8 },
  { x: 136, y: 3 },
  { x: 150, y: 6 },
  { x: 70, y: 5 },
  { x: 84, y: 8 },
]

const CYCLE_MS = 16000

type NinOrgEraBackgroundProps = {
  palette: ScenePalette
}

const buildWindows = (b: Building, y: number, h: number): Array<[number, number]> => {
  const windows: Array<[number, number]> = []
  const top = Math.floor(y) + 2
  const bottom = Math.floor(y + h) - 2
  const left = b.x + 1
  const right = b.x + b.w - 2
  if (bottom <= top || right < left) return windows

  for (let wy = top; wy <= bottom; wy += 2) {
    for (let wx = left; wx <= right; wx += 2) {
      // baby: staggered window pattern per building
      if ((wx + wy + b.x) % 3 !== 0) {
        windows.push([wx, wy])
      }
    }
  }
  return windows
}

/**
 * Ageing-city backdrop with depth layers, varied tower shapes,
 * night neon, windows, and flying cars.
 */
export const NinOrgEraBackground = memo(function NinOrgEraBackground({
  palette: p,
}: NinOrgEraBackgroundProps) {
  const reduceMotion = useReducedMotion()
  const [era, setEra] = useState(0)
  const [clock, setClock] = useState(0)

  useEffect(() => {
    if (reduceMotion) {
      setEra(0)
      setClock(0)
      return
    }

    let frameId = 0
    let start: number | null = null

    const tick = (now: number) => {
      if (start === null) start = now
      const elapsed = now - start
      const t = (elapsed % CYCLE_MS) / CYCLE_MS
      setEra(eraFromTime(t))
      setClock(elapsed / 1000)
      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [reduceMotion])

  const village = 1 - era
  const cyber = era

  // baby: Night city: sky deepens as we age into the future
  const nightSkyTop = '#02060c'
  const nightSkyMid = '#061018'
  const nightSkyLow = '#0a2030'

  const buildingsByLayer = useMemo(() => {
    return LAYER_ORDER.map((layer) =>
      BUILDINGS.filter((b) => b.layer === layer),
    )
  }, [])

  return (
    <svg
      viewBox="0 0 640 360"
      className="absolute inset-0 z-0 h-full w-full"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <linearGradient id="nin-era-sky" x1="0" y1="0" x2="0" y2="1">
          <stop
            offset="0%"
            stopColor={
              // approximate blend via opacity overlay instead
              p.skyTop
            }
          />
          <stop offset="50%" stopColor={p.skyMid} />
          <stop offset="100%" stopColor={p.skyLow} />
        </linearGradient>
        <linearGradient id="nin-night-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={nightSkyTop} />
          <stop offset="45%" stopColor={nightSkyMid} />
          <stop offset="100%" stopColor={nightSkyLow} />
        </linearGradient>
        <linearGradient id="nin-neon-haze" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.neon} stopOpacity="0" />
          <stop offset="70%" stopColor={p.neon} stopOpacity="0.08" />
          <stop offset="100%" stopColor={p.neonSoft} stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* Day sky */}
      <rect width="640" height="360" fill="url(#nin-era-sky)" opacity={village} />
      {/* Night city sky */}
      <rect width="640" height="360" fill="url(#nin-night-sky)" opacity={cyber} />
      {/* Neon haze over night */}
      <rect width="640" height="360" fill="url(#nin-neon-haze)" opacity={cyber} />

      {STARS.map((s) => (
        <rect
          key={`${s.x}-${s.y}`}
          x={px(s.x)}
          y={px(s.y)}
          width={px(1)}
          height={px(1)}
          fill={cyber > 0.4 ? p.neonSoft : p.star}
          opacity={0.4 + cyber * 0.6}
        />
      ))}

      <rect x={px(138)} y={px(6)} width={px(5)} height={px(5)} fill={p.moon} opacity={Math.max(0.2, village)} />
      <rect x={px(10)} y={px(10)} width={px(16)} height={px(3)} fill={p.cloud} opacity={village} />
      <rect x={px(112)} y={px(12)} width={px(18)} height={px(3)} fill={p.cloud} opacity={village} />

      {/* Hills */}
      <rect x={0} y={px(48)} width={px(160)} height={px(12)} fill={p.hillA} />
      <rect x={px(12)} y={px(44)} width={px(32)} height={px(8)} fill={p.hillB} />
      <rect x={px(52)} y={px(42)} width={px(40)} height={px(10)} fill={p.hillA} />
      <rect x={px(100)} y={px(45)} width={px(48)} height={px(8)} fill={p.hillB} />

      {/* Village fields / trees / well */}
      <rect x={px(4)} y={px(50)} width={px(28)} height={px(8)} fill={p.field} opacity={village} />
      <rect x={px(128)} y={px(50)} width={px(28)} height={px(8)} fill={p.field} opacity={village} />
      {[8, 14, 20, 26, 132, 138, 144, 150].map((x) => (
        <rect
          key={`crop-${x}`}
          x={px(x)}
          y={px(52)}
          width={px(1)}
          height={px(4)}
          fill={p.foliage}
          opacity={village}
        />
      ))}
      {[
        { tx: 8, cx: 6 },
        { tx: 48, cx: 46 },
        { tx: 108, cx: 106 },
        { tx: 150, cx: 148 },
      ].map((tree) => (
        <g key={`tree-${tree.tx}`} opacity={village}>
          <rect x={px(tree.tx)} y={px(48)} width={px(2)} height={px(6)} fill={p.foliageDark} />
          <rect x={px(tree.cx)} y={px(46)} width={px(6)} height={px(4)} fill={p.foliage} />
        </g>
      ))}
      <rect x={px(76)} y={px(50)} width={px(6)} height={px(4)} fill={p.stoneMid} opacity={village} />
      <rect x={px(77)} y={px(49)} width={px(4)} height={px(1)} fill={p.stoneLight} opacity={village} />

      {/* Buildings by depth: far → mid → near */}
      {buildingsByLayer.map((layerBuildings, layerIndex) => {
        const layer = LAYER_ORDER[layerIndex] ?? 'mid'
        const layerOp = LAYER_OPACITY[layer]
        const bodyOpacity = lerp(layerOp.village, layerOp.cyber, era)

        return (
          <g key={layer} opacity={bodyOpacity}>
            {layerBuildings.map((b) => {
              const y = lerp(b.vY, b.cY, era)
              const h = lerp(b.vH, b.cH, era)
              const fill = era < 0.35 ? p.hut : p.tower
              const topY = y
              const bodyH = h

              // baby: shape extras only fully visible in cyber era
              const shapeStrength = cyber

              const windows = cyber > 0.15 ? buildWindows(b, y, h) : []

              return (
                <g key={b.id}>
                  {/* Main body */}
                  <rect
                    x={px(b.x)}
                    y={px(topY)}
                    width={px(b.w)}
                    height={px(bodyH)}
                    fill={fill}
                  />

                  {/* Village roof */}
                  <rect
                    x={px(b.x - 1)}
                    y={px(b.vY - 2)}
                    width={px(b.w + 2)}
                    height={px(2)}
                    fill={p.hutRoof}
                    opacity={village}
                  />

                  {/* Shape: stepped setback */}
                  {b.shape === 'step' || b.shape === 'wide' ? (
                    <rect
                      x={px(b.x + 1)}
                      y={px(topY - 3)}
                      width={px(Math.max(2, b.w - 2))}
                      height={px(3)}
                      fill={p.tower}
                      opacity={shapeStrength}
                    />
                  ) : null}

                  {/* Shape: wide shoulder */}
                  {b.shape === 'wide' ? (
                    <rect
                      x={px(b.x - 1)}
                      y={px(topY + bodyH * 0.35)}
                      width={px(b.w + 2)}
                      height={px(3)}
                      fill={p.tower}
                      opacity={shapeStrength * 0.9}
                    />
                  ) : null}

                  {/* Shape: spire */}
                  {b.shape === 'spire' ? (
                    <>
                      <rect
                        x={px(b.x + Math.floor(b.w / 2) - 1)}
                        y={px(topY - 6)}
                        width={px(2)}
                        height={px(6)}
                        fill={p.neon}
                        opacity={shapeStrength}
                      />
                      <rect
                        x={px(b.x + Math.floor(b.w / 2))}
                        y={px(topY - 8)}
                        width={px(1)}
                        height={px(2)}
                        fill={p.neonSoft}
                        opacity={shapeStrength}
                      />
                    </>
                  ) : null}

                  {/* Shape: antenna mast */}
                  {b.shape === 'antenna' ? (
                    <>
                      <rect
                        x={px(b.x + Math.floor(b.w / 2))}
                        y={px(topY - 7)}
                        width={px(1)}
                        height={px(7)}
                        fill={p.neonSoft}
                        opacity={shapeStrength}
                      />
                      <rect
                        x={px(b.x + Math.floor(b.w / 2) - 1)}
                        y={px(topY - 8)}
                        width={px(3)}
                        height={px(1)}
                        fill={p.neon}
                        opacity={shapeStrength * (0.5 + 0.5 * Math.sin(clock * 6 + b.x))}
                      />
                    </>
                  ) : null}

                  {/* Neon crown bar */}
                  <rect
                    x={px(b.x)}
                    y={px(topY)}
                    width={px(b.w)}
                    height={px(1)}
                    fill={p.neon}
                    opacity={shapeStrength * 0.85}
                  />

                  {/* Windows: only on visible body */}
                  {windows.map(([wx, wy]) => (
                    <rect
                      key={`${b.id}-w-${wx}-${wy}`}
                      x={px(wx)}
                      y={px(wy)}
                      width={px(1)}
                      height={px(1)}
                      fill={(wx + wy) % 4 === 0 ? p.neonSoft : p.neon}
                      opacity={cyber * (layer === 'far' ? 0.55 : layer === 'mid' ? 0.85 : 1)}
                    />
                  ))}
                </g>
              )
            })}
          </g>
        )
      })}

      {/* Holo billboards: mid depth */}
      <rect
        x={px(58)}
        y={px(28)}
        width={px(12)}
        height={px(6)}
        fill={p.neon}
        opacity={cyber * 0.9}
      />
      <rect
        x={px(60)}
        y={px(30)}
        width={px(8)}
        height={px(2)}
        fill={p.neonSoft}
        opacity={cyber}
      />
      <rect
        x={px(86)}
        y={px(30)}
        width={px(12)}
        height={px(6)}
        fill={p.neonSoft}
        opacity={cyber * 0.9}
      />
      <rect
        x={px(88)}
        y={px(32)}
        width={px(8)}
        height={px(2)}
        fill={p.neon}
        opacity={cyber}
      />

      {/* Flying cars: continuous motion, fade in with cyber era */}
      {FLYING_CARS.map((car) => {
        const travel = ((clock * car.speed) % 180) + (car.speed > 0 ? -10 : 170)
        const x = car.speed > 0 ? travel : 170 - ((clock * Math.abs(car.speed)) % 180)
        const layerFade =
          car.layer === 'far' ? 0.45 : car.layer === 'mid' ? 0.75 : 1
        const op = cyber * layerFade
        if (op < 0.05) return null
        return (
          <g key={car.id} opacity={op}>
            {/* Trail */}
            <rect
              x={px(car.speed > 0 ? x - car.trail : x + car.w)}
              y={px(car.y)}
              width={px(car.trail)}
              height={px(1)}
              fill={p.neonSoft}
              opacity={0.5}
            />
            {/* Body */}
            <rect
              x={px(x)}
              y={px(car.y)}
              width={px(car.w)}
              height={px(2)}
              fill={p.neon}
            />
            {/* Cockpit glow */}
            <rect
              x={px(car.speed > 0 ? x + car.w - 2 : x)}
              y={px(car.y)}
              width={px(2)}
              height={px(2)}
              fill={p.neonSoft}
            />
          </g>
        )
      })}

      {/* Neon horizon */}
      <rect
        x={0}
        y={px(47)}
        width={px(160)}
        height={px(1)}
        fill={p.neon}
        opacity={cyber}
      />

      {/* Ground */}
      <rect x={0} y={px(58)} width={px(160)} height={px(32)} fill={p.ground} />

      {/* Street grid: near layer feel */}
      {[60, 64, 68, 72, 76, 80, 84].map((y) => (
        <g key={`grid-${y}`}>
          <rect
            x={0}
            y={px(y)}
            width={px(64)}
            height={px(1)}
            fill={p.neon}
            opacity={cyber * 0.45}
          />
          <rect
            x={px(96)}
            y={px(y)}
            width={px(64)}
            height={px(1)}
            fill={p.neon}
            opacity={cyber * 0.45}
          />
        </g>
      ))}
      {[4, 12, 20, 28, 36, 44, 52, 104, 112, 120, 128, 136, 144, 152].map((x) => (
        <rect
          key={`gv-${x}`}
          x={px(x)}
          y={px(58)}
          width={px(1)}
          height={px(32)}
          fill={p.neon}
          opacity={cyber * 0.35}
        />
      ))}

      {/* Path */}
      <rect x={px(66)} y={px(58)} width={px(28)} height={px(32)} fill={p.path} />
      <rect
        x={px(65)}
        y={px(58)}
        width={px(1)}
        height={px(32)}
        fill={cyber > 0.4 ? p.neonSoft : p.pathEdge}
      />
      <rect
        x={px(94)}
        y={px(58)}
        width={px(1)}
        height={px(32)}
        fill={cyber > 0.4 ? p.neonSoft : p.pathEdge}
      />
      {[60, 64, 68, 72, 76, 80, 84].map((y, i) => (
        <rect
          key={`path-${y}`}
          x={px(67 + i)}
          y={px(y)}
          width={px(26 - i * 2)}
          height={px(1)}
          fill={p.pathEdge}
        />
      ))}
    </svg>
  )
})

export type { ScenePalette }
